import type { ApiSuccess, Paginated } from './api';
import type { BookingLifecycleStatus } from './booking';
import type { EventRegistrationStatus } from './event-registration';
import type { FacilityProposalStatus } from './proposal';

export const CUSTOMER_CATEGORY_KEYS = [
  'member',
  'student',
  'graduate',
  'freelancer',
  'teacher',
  'business',
  'visitor',
  'company',
] as const;
export type CustomerCategoryKey = (typeof CUSTOMER_CATEGORY_KEYS)[number];

export interface CustomerCategory {
  id: string;
  key: CustomerCategoryKey;
  name: string;
}

export interface CustomerCommunicationPreferences {
  email: boolean;
  whatsapp: boolean;
  sms: boolean;
  updatedAt: string;
  updatedByUserId: string | null;
}

export interface AdminCustomer {
  id: string;
  reference: string;
  name: string;
  email: string;
  phone: string;
  category: CustomerCategory;
  tags: string[];
  interests: string[];
  communicationPreferences: CustomerCommunicationPreferences;
  internalNotes: string;
  joinedAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAdminCustomerRequest {
  name: string;
  email?: string;
  phone?: string;
  categoryKey?: CustomerCategoryKey;
  tags?: string[];
  interests?: string[];
  communicationPreferences?: Pick<CustomerCommunicationPreferences, 'email' | 'whatsapp' | 'sms'>;
  internalNotes?: string;
}

export type UpdateAdminCustomerRequest = Partial<CreateAdminCustomerRequest>;

/**
 * Interests offered as one-click picks: in the admin editor's suggestions and
 * as the boxes on the public sign-up page. Both still take free text.
 */
export const CUSTOMER_INTEREST_SUGGESTIONS = [
  'Coworking',
  'Podcasting',
  'Design',
  'Startups',
  'Photography',
  'Teaching',
  'Marketing',
  'Software',
] as const;

/** The tag a record created from the public sign-up page carries, so staff can filter on it. */
export const CUSTOMER_SIGN_UP_TAG = 'Signed up online';

/**
 * Categories a visitor may pick for themselves. `member` is left out: it is a
 * status staff grant, not a description someone gives of themselves.
 */
export const SELF_SERVICE_CATEGORY_KEYS = [
  'student',
  'graduate',
  'freelancer',
  'teacher',
  'business',
  'company',
  'visitor',
] as const satisfies readonly CustomerCategoryKey[];
export type SelfServiceCategoryKey = (typeof SELF_SERVICE_CATEGORY_KEYS)[number];

/**
 * `POST /api/customers/sign-up` — the public link's form. The same record as
 * `CreateAdminCustomerRequest`, less what only staff set: tags and notes.
 */
export interface CustomerSignUpRequest {
  name: string;
  email: string;
  phone?: string;
  categoryKey: SelfServiceCategoryKey;
  interests: string[];
  communicationPreferences: Pick<CustomerCommunicationPreferences, 'email' | 'whatsapp' | 'sms'>;
}

/** Says only that it was taken: never whether the email already had a record. */
export interface CustomerSignUpReceipt {
  received: true;
}
export type CustomerSignUpReceiptResponse = ApiSuccess<CustomerSignUpReceipt>;

export type AdminCustomerListResponse = Paginated<AdminCustomer>;
export interface AdminCustomerResponse {
  data: AdminCustomer;
}

interface CustomerHistoryBase {
  id: string;
  reference: string;
  /** When this interaction first entered the system. */
  occurredAt: string;
  /** The date the booking, event, or proposal is scheduled for. */
  scheduledDate: string;
  startMinute: number | null;
  endMinute: number | null;
  title: string;
  detail: string;
  amountMinor: number | null;
  currency: 'USD';
}

export interface CustomerBookingHistoryItem extends CustomerHistoryBase {
  type: 'booking';
  status: BookingLifecycleStatus;
  bookingEndDate: string;
  deskLabel: string | null;
}

export interface CustomerEventHistoryItem extends CustomerHistoryBase {
  type: 'event';
  eventId: string;
  status: EventRegistrationStatus;
  seatCount: number;
  attended: boolean;
  checkedInAt: string | null;
}

export interface CustomerProposalHistoryItem extends CustomerHistoryBase {
  type: 'proposal';
  status: FacilityProposalStatus;
  attendeeCount: number;
  bookingReference: string | null;
}

export type CustomerHistoryItem =
  CustomerBookingHistoryItem | CustomerEventHistoryItem | CustomerProposalHistoryItem;

export interface CustomerHistorySummary {
  bookingCount: number;
  eventRegistrationCount: number;
  eventAttendanceCount: number;
  proposalCount: number;
}

export interface AdminCustomerHistoryResponse extends Paginated<CustomerHistoryItem> {
  summary: CustomerHistorySummary;
}
